import { useRef, useCallback } from 'react'
import { useMutation } from '@apollo/client'
import { Messaging } from 'firebase/messaging'
import { SendFirebaseTokenDocument } from '@app/graphql'
import { getFirebaseMessaging, loadToken, handleForegroundMessages } from '@app/firebase'
import { resetAppUpdateState } from '@app/storage'

export const useFirebasePushSetup = () => {
  const [sendFirebaseToken] = useMutation(SendFirebaseTokenDocument)
  const swRegistration = useRef<ServiceWorkerRegistration | null>(null)
  const firebaseMessagingRef = useRef<Messaging | null>(null)
  const firebaseTokenRef = useRef<string | null>(null)

  const updateServiceWorker = useCallback(() => {
    const registration = swRegistration.current
    if (registration?.waiting) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' })
      resetAppUpdateState()
    }
  }, [])

  const setSWRegistration = useCallback(async () => {
    if (!('serviceWorker' in navigator)) return
    let registration = await navigator.serviceWorker.getRegistration()
    if (!registration) {
      registration = await navigator.serviceWorker.register('/service-worker.js')
    }
    swRegistration.current = registration
  }, [])

  const setupFirebasePush = useCallback(async () => {
    firebaseMessagingRef.current = await getFirebaseMessaging()
    if (!firebaseMessagingRef.current) return

    await setSWRegistration()
    const registration = swRegistration.current
    if (!registration) return

    try {
      if (Notification.permission === 'granted') {
        const token = await loadToken(firebaseMessagingRef.current, registration)
        firebaseTokenRef.current = token
        if(!token) return
        await sendFirebaseToken({ variables: { token } })
        handleForegroundMessages(firebaseMessagingRef.current)
      }

    } catch (err) {
      console.error('Firebase setup failed:', err)
    }

    if (registration.waiting) updateServiceWorker()
  }, [setSWRegistration, updateServiceWorker, sendFirebaseToken])

  return { setupFirebasePush, updateServiceWorker }
}
